import { formatCheckoutTimeSummary, getOnDemandCustomText } from "../../../checkout-v2/domain/timeSummary";

const normalizeId = (value) => {
  if (value === null || value === undefined || value === "") return null;
  return `${value}`;
};

const findById = (items, id) => {
  const normalized = normalizeId(id);
  if (!normalized || !Array.isArray(items)) return null;
  return items.find((item) => normalizeId(item && item.id) === normalized) || null;
};

const localized = (item, language, englishKeys, arabicKeys) => {
  if (!item) return "";
  const keys = language === "arabic" ? arabicKeys.concat(englishKeys) : englishKeys;
  const key = keys.find((candidate) => item[candidate]);
  return key ? `${item[key]}` : "";
};

const getBranchName = (store, branchId, language) =>
  localized(
    findById(store && store.branches, branchId),
    language,
    ["name", "branch_name"],
    ["ar_name", "branch_ar_name"],
  );

export const resolveAreaName = (store, address, branchDeliveryChargeId, language) => {
  const bdc = findById(
    store && store.branch_delivery_charges,
    branchDeliveryChargeId,
  );
  const embeddedArea = address && typeof address.area === "object" ? address.area : null;
  const area = findById(store && store.areas, embeddedArea?.id || address?.area || bdc?.area_id);
  const candidates = [bdc, embeddedArea, area].filter(Boolean);
  // Some branch payloads carry area_ar; store-based payloads carry ar_name.
  // Try all Arabic sources before falling back to an English charge label.
  const arabicKeys = ["area_ar_name", "ar_area_name", "area_ar", "ar_area", "ar_name", "name_ar", "delivery_charge__area__ar_name"];
  const englishKeys = ["area_name", "area", "name", "delivery_charge__area__name"];
  const readName = (keys) => {
    for (const candidate of candidates) {
      for (const key of keys) {
        const value = candidate[key];
        if (typeof value === "string" && value.trim()) return value.trim();
      }
    }
    return "";
  };
  return (language === "arabic" && readName(arabicKeys)) || readName(englishKeys);
};

export const resolveContextSummary = ({
  store = {},
  settings = {},
  branch = {},
  address = {},
  language = "english",
} = {}) => {
  store = store || {};
  branch = branch || {};
  address = address || {};
  const isPickup =
    branch.isPickup === true || Number(address.selectedDeliveryType) === 1;
  const branchId = branch.branch || address.pickupArea;
  const branchRecord = findById(store && store.branches, branchId);
  const branchDeliveryCharge = findById(
    store && store.branch_delivery_charges,
    branch.bdc,
  );
  const branchName = getBranchName(store, branchId, language);
  const areaName = resolveAreaName(store, address, branch.bdc, language);
  const isBranchBased =
    settings.is_branch_based_ordering === true ||
    store?.settings?.is_branch_based_ordering === true;
  const hasContext = isPickup
    ? Boolean(branchId)
    : isBranchBased
      ? Boolean(branchId && branch.bdc)
      : Boolean(address.area || branch.bdc);
  const method = isPickup
    ? language === "arabic"
      ? "استلام"
      : "Pickup"
    : language === "arabic"
    ? "توصيل"
    : "Delivery";
  const place = isPickup ? branchName : areaName || branchName;

  return {
    resolved: hasContext,
    isPickup,
    branchId: normalizeId(branchId),
    branchDeliveryChargeId: normalizeId(branch.bdc),
    minimumOrderValue: isPickup
      ? branchRecord?.minimum_order_value
      : branchDeliveryCharge?.minimum_order_value,
    method,
    place,
    label: hasContext && place ? `${method} · ${place}` : method,
  };
};

export const resolveMinimumOrder = ({ settings = {}, context = {} } = {}) => {
  const raw =
    context.minimumOrderValue !== null &&
    context.minimumOrderValue !== undefined
      ? context.minimumOrderValue
      : context.isPickup
      ? settings.minimum_order_pickup
      : settings.minimum_order;
  const minimum = Number(raw);
  return Number.isFinite(minimum) && minimum > 0 ? minimum : 0;
};

export const resolveCountrySummary = ({
  store = {},
  address = {},
  language = "english",
} = {}) => {
  store = store || {};
  address = address || {};
  const configuredCountries = Array.isArray(store.all_countries)
    ? store.all_countries
    : Array.isArray(store.countries)
    ? store.countries
    : [];
  const countries = configuredCountries.filter(
    (country) =>
      country &&
      country.is_active !== false &&
      country.is_deleted !== true,
  );
  const selected =
    (address.country && typeof address.country === "object"
      ? address.country
      : findById(countries, address.country)) ||
    (countries.length === 1 ? countries[0] : null);
  return {
    multiple: countries.length > 1,
    selected,
    label: localized(
      selected,
      language,
      ["name", "country_name", "dial_code"],
      ["ar_name", "country_ar_name"],
    ),
  };
};

export const resolveMovProgress = ({ minimum = 0, cartTotal = 0 } = {}) => {
  const safeMinimum = Math.max(Number(minimum) || 0, 0);
  const safeTotal = Math.max(Number(cartTotal) || 0, 0);
  const remaining = Math.max(safeMinimum - safeTotal, 0);
  return {
    minimum: safeMinimum,
    total: safeTotal,
    remaining,
    complete: safeMinimum > 0 && remaining === 0,
    progress:
      safeMinimum > 0 ? Math.min(Math.max(safeTotal / safeMinimum, 0), 1) : 0,
  };
};

const normalizeMinutes = (value) => {
  const minutes = Number(value);
  return Number.isFinite(minutes) && minutes >= 0 ? Math.round(minutes) : null;
};

export const resolveTimeSummary = ({
  deliveryTime = {},
  language = "english",
  settings = {}, branch, cart = {}, fulfillmentType,
} = {}) => {
  const time = deliveryTime || {};
  const type = time.type || (time.asap ? "asap" : null);
  if (type === "asap") {
    const custom = getOnDemandCustomText({ settings, branch, cart, language,
      type: fulfillmentType || (time.isPickup ? "pickup" : "delivery") });
    if (custom) return custom;
    const within = normalizeMinutes(time.within ?? time.asap?.within);
    if (language === "arabic")
      return within === null ? "بأسرع وقت" : `بأسرع وقت · حوالي ${within} دقيقة`;
    return within === null ? "ASAP" : `ASAP · ~${within} min`;
  }
  if (type === "scheduled" && (time.expected_start || time.expected_end)) {
    const window = formatCheckoutTimeSummary({
      type: "scheduled", date: time.expected_date,
      start: time.expected_start, end: time.expected_end,
    }, language === "arabic");
    return language === "arabic" ? `موعد · ${window}` : `Scheduled · ${window}`;
  }
  return language === "arabic" ? "اختر الوقت" : "Choose time";
};
